import { parseAmount } from "../../../../../../../utils/number/parse-amount.ts"

type MilestonePaymentStatus = "none" | "pending" | "partial" | "completed"

// Narrower than antd's LiteralUnion, which accepts any string: a typo'd colour
// would otherwise only surface as an uncoloured tag at runtime.
type MilestoneTagColor = "default" | "blue" | "cyan" | "green"

// Deliberately diverges from the backend's _determine_status: it reads the
// exercised figures the row renders (payable_status is computed before
// exercised top-ups fold in, so the two can disagree), and it declines to call
// a net-refund row settled where the backend's `total_completed >= total_value`
// would.
const getMilestonePaymentStatus = (
  total: string,
  completed: string
): MilestonePaymentStatus => {
  const totalNum = parseAmount(total)
  const completedNum = parseAmount(completed)

  // A negative total is a net refund — money owed back, not a plan to settle
  // against. Neither "settled" nor "part-settled" describes it.
  if (totalNum < 0) return "none"

  // No planned value, but money moved: settled, matching the backend's
  // "milestones without a planned receivable are paid once any receipt exists".
  if (totalNum === 0) return completedNum > 0 ? "completed" : "none"

  if (completedNum >= totalNum) return "completed"
  if (completedNum > 0) return "partial"
  return "pending"
}

const MILESTONE_PAYMENT_TAG_COLORS: Record<
  MilestonePaymentStatus,
  MilestoneTagColor
> = {
  none: "default",
  pending: "blue",
  partial: "cyan",
  completed: "green"
}

const MILESTONE_PAYMENT_LABEL_KEYS: Record<MilestonePaymentStatus, string> = {
  none: "finance.milestonePaymentStatus.none",
  pending: "finance.milestonePaymentStatus.pending",
  partial: "finance.milestonePaymentStatus.partial",
  completed: "finance.milestonePaymentStatus.completed"
}

export {
  MILESTONE_PAYMENT_LABEL_KEYS,
  MILESTONE_PAYMENT_TAG_COLORS,
  getMilestonePaymentStatus,
  type MilestonePaymentStatus
}
